import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { takeUntil } from 'rxjs/operators';
import { eventFindInPath, findClosestElement, isDefined } from '@fi-shared/utils';
import { TextEditorSelectionElementBuilderService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-selection-element-builder.service';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { AppTextService } from '@fi-shared/app-text';
import {
    InsertTableItemDirectionEnum,
    TablePanelActionIconsEnum,
} from '@fi-app/utilities/fi-text-item-table-panel-handler/fi-text-item-table-panel-enum';
import { ITablePanelOption } from '@fi-app/utilities/fi-text-item-table-panel-handler/fi-text-item-table-panel';
import { isNullOrUndefined } from 'util';

@Component({
    selector: 'fi-text-item-table-panel-handler',
    templateUrl: './fi-text-item-table-panel-handler.component.html',
    styleUrls: ['./fi-text-item-table-panel-handler.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-text-item-table-panel-handler' },
    standalone: false,
})
export class FiTextItemTablePanelHandlerComponent implements OnInit, OnDestroy {
    @Input() editableElement: HTMLElement;
    @HostBinding('style.visibility') get panelVisibilityStyle(): string {
        return this.currentTextItemTableBody$.value && !this.isTypingProcess ? 'visible' : 'hidden';
    }

    currentTextItemTableBody$: BehaviorSubject<HTMLElement> = new BehaviorSubject<HTMLElement>(undefined);
    FiInfoIconMode = FiInfoIconMode;
    tablePanelOptions: ITablePanelOption[];
    currentTooltipVisibleId$ = new Subject<number>();
    private isTypingProcess = false;

    constructor(
        private fiBrowserEventsService: FiBrowserEventsService,
        private elementRef: ElementRef,
        private textEditorSelectionElementBuilderService: TextEditorSelectionElementBuilderService,
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
        private renderer2: Renderer2,
    ) {}

    ngOnInit() {
        this.setTablePanelOptions();
        this.handleClickEvent();
        this.handleTableItemUpdate();
    }

    private handleClickEvent() {
        this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((event) => {
                const target: HTMLElement = event.target as HTMLElement;
                // ignore click on panel and menu items
                if (findClosestElement(target, this.elementRef.nativeElement) || this.isIgnored(target)) {
                    return;
                }
                if (findClosestElement(target, this.editableElement)) {
                    const tableElement: HTMLElement = target.closest('table[fi-table-text-item]');
                    this.currentTextItemTableBody$.next(tableElement?.firstChild as HTMLElement);
                    this.isTypingProcess = false;
                    return;
                }
                this.currentTextItemTableBody$.next(undefined);
            });
        this.fiBrowserEventsService
            .getDocumentEventSubject$('keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((event: KeyboardEvent) => {
                if (event.key === 'Tab') {
                    event.preventDefault();
                    event.stopPropagation();
                    if (isDefined(this.getFocusedTableCellFromRange())) this.focusNextTableDataCell();
                } else {
                    this.isTypingProcess = true;
                }
            });
    }

    private isIgnored(target: HTMLElement): boolean {
        const ignoreClasses = ['.mat-mdc-menu-item'];
        return ignoreClasses.some((ignoreClass) => !!eventFindInPath({ target }, ignoreClass));
    }

    private handleTableItemUpdate() {
        this.currentTextItemTableBody$.takeUntil(this.ngOnDestroy$).subscribe((tableBodyElement) => {
            this.cdr.markForCheck();
            this.setPanelPosition(tableBodyElement?.parentElement);
        });
    }

    private focusNextTableDataCell() {
        if (!this.currentTextItemTableBody$.value) return;

        const currentTableCell = this.getFocusedTableCellFromRange();
        if (!isNullOrUndefined(currentTableCell.nextSibling)) {
            this.focusTableCell(currentTableCell.nextElementSibling as HTMLElement);
        } else {
            if (isNullOrUndefined(currentTableCell.parentElement.nextSibling)) return;
            this.focusTableCell(currentTableCell.parentElement.nextSibling.firstChild as HTMLElement);
        }
    }

    private focusTableCell(tableCell: HTMLElement) {
        const range = new Range();
        range.setStart(tableCell, 0);
        range.setEnd(tableCell, 0);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        this.textEditorSelectionElementBuilderService.setCurrentRange(range);
    }

    private setPanelPosition(tableElement: HTMLElement) {
        if (tableElement) {
            const panelElement = this.elementRef.nativeElement;
            this.renderer2.setStyle(panelElement, 'top', tableElement.offsetTop - panelElement.offsetHeight - 5 + 'px');
            this.renderer2.setStyle(panelElement, 'left', tableElement.offsetLeft + 4 + 'px');
        }
    }

    private toggleHeaderRow() {
        const tableBody = this.currentTextItemTableBody$.value;

        if (this.isTableHeaderEnabled()) {
            tableBody.removeChild(tableBody.firstChild);
        } else {
            tableBody.insertBefore(this.getNewTableHeaderElement(tableBody), tableBody.childNodes.item(0));
        }
        this.currentTextItemTableBody$.next(tableBody);
    }

    private insertRow(insertRowDirection: InsertTableItemDirectionEnum) {
        const tableBody = this.currentTextItemTableBody$.value;
        const currentTableCell = this.getFocusedTableCellFromRange();
        const newRow = document.createElement('tr');
        const currentTableRow = currentTableCell.parentElement;

        currentTableRow.childNodes.forEach(() => newRow.appendChild(document.createElement('td')));

        tableBody.insertBefore(
            newRow,
            this.isInsertTableDirectionBefore(insertRowDirection) ? currentTableRow : currentTableRow.nextSibling,
        );

        if (this.isTableElementLastChild() && !this.isInsertTableDirectionBefore(insertRowDirection)) {
            tableBody.appendChild(newRow);
        } else {
            tableBody.insertBefore(
                newRow,
                this.isInsertTableDirectionBefore(insertRowDirection) ? currentTableRow : currentTableRow.nextSibling,
            );
        }

        this.currentTextItemTableBody$.next(tableBody);
    }

    private deleteRow() {
        const tableBody = this.currentTextItemTableBody$.value;
        const currentTableRow = this.getFocusedTableCellFromRange().parentElement;

        tableBody.removeChild(currentTableRow);
        this.checkRemoveEmptyTable(tableBody);
        this.currentTextItemTableBody$.next(undefined);
    }

    private insertColumn(insertColumnDirection: InsertTableItemDirectionEnum) {
        const tableBody = this.currentTextItemTableBody$.value;
        const currentTableCell = this.getFocusedTableCellFromRange();

        if (this.isTableElementLastChild() && !this.isInsertTableDirectionBefore(insertColumnDirection)) {
            tableBody.childNodes.forEach((childNode, index) => {
                childNode.appendChild(this.getNewTableCellElement(index));
            });
        } else {
            const currentSelectedTableItem = this.isInsertTableDirectionBefore(insertColumnDirection)
                ? currentTableCell
                : currentTableCell.nextSibling;
            let currentSelectedTableItemIndex = Array.from(currentSelectedTableItem.parentElement.childNodes).indexOf(
                currentSelectedTableItem,
            );

            tableBody.childNodes.forEach((childNode, index) => {
                const insertBeforeChildNode = childNode.childNodes.item(currentSelectedTableItemIndex);
                childNode.insertBefore(this.getNewTableCellElement(index), insertBeforeChildNode);
            });
        }
        this.currentTextItemTableBody$.next(tableBody);
    }

    private getNewTableCellElement(childIndex: number): HTMLElement {
        return document.createElement(childIndex === 0 && this.isTableHeaderEnabled() ? 'th' : 'td');
    }

    private deleteColumn() {
        const tableBody = this.currentTextItemTableBody$.value;
        const currentSelectedTableItem = this.getFocusedTableCellFromRange();
        const currentSelectedTableItemIndex = Array.from(currentSelectedTableItem.parentElement.childNodes).indexOf(
            currentSelectedTableItem,
        );

        tableBody.childNodes.forEach((childNode, index) => {
            childNode.removeChild(childNode.childNodes.item(currentSelectedTableItemIndex));
        });

        this.checkRemoveEmptyTable(tableBody);
        this.currentTextItemTableBody$.next(undefined);
    }

    private deleteTable() {
        const table = this.currentTextItemTableBody$.value.parentElement;
        table.parentElement.removeChild(table);
        this.currentTextItemTableBody$.next(undefined);
    }

    private getNewTableHeaderElement(trContainer: Element): HTMLElement {
        const tableHeaderRow = document.createElement('tr');
        trContainer.childNodes.item(0).childNodes.forEach(() => {
            tableHeaderRow.appendChild(document.createElement('th'));
        });
        return tableHeaderRow;
    }

    private getFocusedTableCellFromRange(): HTMLElement {
        const currentRange: Range = this.textEditorSelectionElementBuilderService.getCurrentRange();
        const selectionStartContainer = currentRange.startContainer;

        // selectionStartContainer might equal to tbody after removing row/column and focusing empty td element on row beginning;
        if (selectionStartContainer.nodeName.toLowerCase() === 'tbody')
            return this.currentTextItemTableBody$.value.childNodes.item(currentRange.startOffset)
                .firstChild as HTMLElement;

        if (selectionStartContainer.nodeType === Node.TEXT_NODE)
            return this.getElementClosestTableCell((selectionStartContainer as HTMLElement).parentElement);

        return this.getElementClosestTableCell(selectionStartContainer as HTMLElement);
    }

    private getElementClosestTableCell(element: HTMLElement): HTMLElement {
        return element.closest('td') || element.closest('th');
    }

    private isTableHeaderEnabled(): boolean {
        return this.currentTextItemTableBody$.value?.firstChild?.firstChild?.nodeName.toLowerCase() === 'th';
    }

    private getHeaderIconSrc(): string {
        return this.isTableHeaderEnabled()
            ? TablePanelActionIconsEnum.HeaderEnabled
            : TablePanelActionIconsEnum.HeaderDisabled;
    }

    private getHeaderIconTooltip(): string {
        return this.appTextService.get(this.isTableHeaderEnabled() ? 'table.header_disable' : 'table.header_enable');
    }

    private isInsertTableDirectionBefore(insertColumnDirection: InsertTableItemDirectionEnum): boolean {
        return insertColumnDirection === InsertTableItemDirectionEnum.Before;
    }

    private isTableElementLastChild(): boolean {
        const focusedTableCellFromRange = this.getFocusedTableCellFromRange();
        return (
            Array.from(focusedTableCellFromRange.parentElement.childNodes).indexOf(focusedTableCellFromRange) ===
            focusedTableCellFromRange.parentElement.childNodes.length - 1
        );
    }

    private checkRemoveEmptyTable(tableBody: HTMLElement) {
        if (!tableBody.childNodes.length || !tableBody.childNodes.item(0).childNodes.length) {
            this.deleteTable();
        } else {
            this.currentTextItemTableBody$.next(tableBody);
        }
    }

    setCurrentTooltipVisibleId(id?: number) {
        this.currentTooltipVisibleId$.next(id);
    }

    private isInsertBeforeRowDisabled(): boolean {
        if (!this.isTableHeaderEnabled()) return false;

        return this.getFocusedTableCellFromRange()?.nodeName.toLowerCase() === 'th';
    }

    private setTablePanelOptions() {
        this.tablePanelOptions = [
            {
                id: 0,
                getButtonImgSrc: () => this.getHeaderIconSrc(),
                tooltipText: this.getHeaderIconTooltip(),
                onClick: () => this.toggleHeaderRow(),
            },
            {
                id: 1,
                getButtonImgSrc: () => TablePanelActionIconsEnum.RowOptions,
                tooltipText: this.appTextService.get('table.row'),
                isMenuTrigger: true,
                menuItems: [
                    {
                        label: this.appTextService.get('table.action_insert_after'),
                        onClick: () => this.insertRow(InsertTableItemDirectionEnum.After),
                    },
                    {
                        label: this.appTextService.get('table.action_insert_before'),
                        onClick: () => this.insertRow(InsertTableItemDirectionEnum.Before),
                        isDisabled: () => this.isInsertBeforeRowDisabled(),
                    },
                    {
                        label: this.appTextService.get('common.delete'),
                        onClick: () => this.deleteRow(),
                    },
                ],
            },
            {
                id: 2,
                getButtonImgSrc: () => TablePanelActionIconsEnum.ColumnOptions,
                tooltipText: this.appTextService.get('table.column'),
                isMenuTrigger: true,
                menuItems: [
                    {
                        label: this.appTextService.get('table.action_insert_after'),
                        onClick: () => this.insertColumn(InsertTableItemDirectionEnum.After),
                    },
                    {
                        label: this.appTextService.get('table.action_insert_before'),
                        onClick: () => this.insertColumn(InsertTableItemDirectionEnum.Before),
                    },
                    {
                        label: this.appTextService.get('common.delete'),
                        onClick: () => this.deleteColumn(),
                    },
                ],
            },
            {
                id: 3,
                getButtonImgSrc: () => TablePanelActionIconsEnum.TableRemove,
                tooltipText: this.appTextService.get('table.table_remove'),
                onClick: () => this.deleteTable(),
            },
        ];
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
